import { api } from './api';
import type { Experiment, Variation } from './api';
import { clear, field, h } from './dom';

const TYPE_LABEL = {
  control: 'Original page',
  changeset: 'Changes the page',
  redirect: 'Sends to another page',
} as const;

/** The variation that carries exactly one section's option (what the visual editor edits). */
export function sourceVariation(
  exp: Experiment,
  factorId: string,
  optionId: string,
): Variation | undefined {
  const factors = exp.factors ?? [];
  return exp.variations.find(
    (v) =>
      v.levels?.[factorId] === optionId &&
      factors.every((f) => f.id === factorId || v.levels?.[f.id] === f.options[0]!.id),
  );
}

/**
 * "Page to edit", the variations (or, for a multivariate test, the sections and their options), and
 * a button per editable thing that opens the visual editor on the customer's own site.
 */
export function variationsBlock(
  exp: Experiment,
  ctx: { signal: AbortSignal },
  tail: HTMLElement,
  into: HTMLElement,
  action: (
    btn: HTMLButtonElement,
    out: HTMLElement,
    fn: () => Promise<void>,
    fallback?: string,
  ) => void,
): void {
  const page = h('input', {
    type: 'text',
    autocomplete: 'off',
    value: '/',
    placeholder: '/pricing',
  }) as HTMLInputElement;
  const opened = h('div', {});

  const editButton = (v: Variation, label: string) => {
    const edit = h('button', { type: 'button' }, `Edit ${label}`);
    action(
      edit,
      opened,
      async () => {
        const r = await api<{ editUrl: string }>(
          'POST',
          `/experiments/${exp.id}/variations/${v.id}/editor-token`,
          { path: page.value || '/' },
        );
        window.open(r.editUrl, '_blank', 'noopener,noreferrer');
        clear(opened);
        opened.append(
          h(
            'p',
            { class: 'muted' },
            'The editor opens on your site in a new tab. ',
            h(
              'a',
              { href: r.editUrl, target: '_blank', rel: 'noopener noreferrer' },
              'Open the editor',
            ),
            ' if nothing opened.',
          ),
        );
      },
      'Could not open the editor.',
    );
    return edit;
  };

  if (exp.factors?.length) {
    const sections = exp.factors.map((f) =>
      h(
        'li',
        {},
        h('strong', {}, f.name),
        h(
          'ul',
          { class: 'options' },
          ...f.options.map((o, i) => {
            const li = h('li', {}, i === 0 ? `${o.name} (the page as it is)` : o.name);
            const v = i === 0 ? undefined : sourceVariation(exp, f.id, o.id);
            if (v && v.type === 'changeset') li.append(' ', editButton(v, `${f.name}: ${o.name}`));
            return li;
          }),
        ),
      ),
    );
    into.append(
      h('h2', {}, 'Sections'),
      h(
        'p',
        { class: 'muted' },
        'Edit each option on its own. Every combination below is built from them automatically, and each section must change a different part of the page.',
      ),
      field('Page to edit', page),
      h('ul', { class: 'cards' }, ...sections),
      opened,
      h('h2', {}, `Combinations (${exp.variations.length})`),
      h(
        'ul',
        { class: 'cards combos' },
        ...exp.variations.map((v) =>
          h(
            'li',
            {},
            h('strong', {}, v.name ?? v.type),
            ' ',
            h('span', { class: 'muted' }, `${v.weight}% of visitors`),
          ),
        ),
      ),
      tail,
    );
    return;
  }

  into.append(
    h('h2', {}, 'Variations'),
    field('Page to edit', page),
    h(
      'ul',
      { class: 'cards' },
      ...exp.variations.map((v) => {
        const name = v.name ?? v.type;
        const li = h(
          'li',
          {},
          h('strong', {}, name),
          ' ',
          h('span', { class: 'muted' }, `${TYPE_LABEL[v.type]} · ${v.weight}% weight`),
        );
        if (v.type === 'changeset') li.append(' ', editButton(v, name));
        return li;
      }),
    ),
    opened,
    tail,
  );
  void ctx;
}
